/**
 * The glue between cm-api's stored shapes and `@andavo/staffing-engine`.
 *
 * WHY A GLUE LAYER AT ALL. The API and the engine key CSMs DIFFERENTLY, and the
 * difference is the single most dangerous thing in this module:
 *
 *   - Accounts carry `currentCsm`, already resolved by cm-api to the engine key
 *     (`csm_external_id` → `csm_email` → display name, canonicalized across the
 *     whole book by resolveCsmIdentities).
 *   - Slots and pins carry `csmId`, the `staffing_csm` DB ROW id, because those
 *     are real foreign keys.
 *
 * Handing the engine a mix of the two would not error. It would compare two
 * spellings of one person, find them unequal, and silently split their book —
 * which is precisely the failure the CRM's 2026-08-10 stable-advisor-identity
 * work exists to prevent, and which the engine's own types warn about. So the
 * row id is translated to the engine key HERE, in one place, and `diagnose()`
 * reports every case where that translation is uncertain rather than guessing.
 *
 * Pure functions over plain data — no fetching, no React. That is what lets
 * `model.spec.ts` cover the key-reconciliation cases without a server.
 */
import {
  bucketByTier,
  computePortfolios,
  portfolioLoad,
  SENTINEL_CSMS,
  tierLoads,
  type Account,
  type BalanceBy,
  type Pins,
  type StaffingConfig,
  type StaffingResult,
  type TierDef,
  type TierPins,
} from '@andavo/staffing-engine';
import type {
  PinDto,
  RosterMemberDto,
  SlotDto,
  StaffingReadSet,
  StoredTierDto,
} from '../api';
import type { HypotheticalCsm } from './scenario';

/**
 * The engine key for a roster member.
 *
 * This fallback chain matches the PREFERENCE ORDER in cm-api's `csmKeyOf` —
 * external id, then email, then display name, bare (not namespaced) — and it is
 * restated rather than imported because cm-api is npm-standalone and not
 * importable from apps/web.
 *
 * IT IS NOT THE SAME FUNCTION, and the docblock here used to claim it was.
 * cm-api resolves identities with union-find across the WHOLE book
 * (`resolveCsmIdentities`), so it can link a row carrying only an email to one
 * carrying a stable id and canonicalize both to the id — and it can return null.
 * This is a bare three-way fallback over ONE roster row that never returns null.
 * They agree whenever a roster row carries the identifier cm-api canonicalized
 * to, which is the ordinary case, and diverge when it does not: a person whose
 * roster row has only an email, while their accounts were canonicalized to an
 * external id linked through some other row, gets a key here that matches none
 * of their accounts.
 *
 * THAT FAILURE IS VISIBLE RATHER THAN SILENT, which is the reason this is a
 * documented limitation and not a bug fix: the mismatch shows up as a roster
 * member holding no customers, which `diagnose()` reports as an idle member.
 * The real fix is upstream — the #1802 reconciliation giving every CSM a stable
 * id — after which the two chains cannot disagree.
 *
 * WHAT PINS IT, precisely. `model.spec.ts` asserts THIS chain against fixtures
 * inside apps/web; it imports nothing from cm-api and executes none of its code,
 * so it catches a reorder on this side and cannot catch one on the other. The
 * tier contract is pinned properly by cm-api's own guardrail; this one is not,
 * and saying so is better than a comment that implies a guarantee nobody wrote.
 * Caught in review by @taylordaily on #1929.
 */
export function csmKeyOf(member: {
  externalId: string | null;
  email: string | null;
  displayName: string;
}): string {
  return member.externalId ?? member.email ?? member.displayName;
}

/**
 * Stored tiers → engine tiers. The ONE place `minSpendM: null` becomes
 * -Infinity, which is what makes the lowest tier a true catch-all so that
 * zero-and-negative-spend accounts (credits, adjustments — 19 of them in the
 * reference snapshot) land in a book instead of being dropped.
 */
export function toEngineTiers(tiers: readonly StoredTierDto[]): TierDef[] {
  return tiers.map((t) => {
    const named = t.selection === 'named';
    return {
      key: t.key,
      label: t.label,
      // `null` means OPPOSITE things for the two kinds, which is the whole
      // reason the kind is stored rather than inferred:
      //   band  → -Infinity, the catch-all: everything below the last floor.
      //   named → +Infinity, unreachable: nothing lands here except by hand.
      // Collapsing them would make a strategic tier quietly absorb every credit
      // and adjustment in the book.
      minSpendM: named
        ? Number.POSITIVE_INFINITY
        : t.minSpendM === null
          ? Number.NEGATIVE_INFINITY
          : t.minSpendM,
      hoursPerAccountPerMonth: t.hoursPerAccountPerMonth,
      selection: named ? ('named' as const) : ('band' as const),
    };
  });
}

/** Row id → engine key, for translating slots and pins. */
export function rosterKeyIndex(
  roster: readonly RosterMemberDto[],
): Map<string, string> {
  return new Map(roster.map((m) => [m.id, csmKeyOf(m)]));
}

/**
 * Engine key → `staffing_csm` ROW id. The mirror of rosterKeyIndex.
 *
 * Pinning needs this direction: a portfolio is keyed by the engine key, and
 * `POST /api/staffing/pins` takes the row id, because that is the real foreign
 * key. Without it the UI would have to send a key the route cannot resolve.
 */
export function rosterIdIndex(
  roster: readonly RosterMemberDto[],
): Map<string, string> {
  return new Map(roster.map((m) => [csmKeyOf(m), m.id]));
}

/** Engine key → display label, preferring the roster's own name over the CRM's. */
export function displayNameIndex(
  roster: readonly RosterMemberDto[],
  csmDisplayNames: Readonly<Record<string, string>>,
): Map<string, string> {
  const out = new Map<string, string>(Object.entries(csmDisplayNames));
  for (const m of roster) out.set(csmKeyOf(m), m.displayName);
  return out;
}

/**
 * Slots → the engine's `Record<tierKey, CsmId[]>`, in stored position order.
 *
 * VACANCIES ARE DROPPED, deliberately. A slot with no `csmId` is a planned hire
 * — a real and useful thing to show a manager — but the engine distributes work
 * to the people in a tier's slot list, and inventing a placeholder person there
 * would hand a book of real customers to someone who does not exist. The vacancy
 * count is reported by `diagnose()` and rendered separately instead.
 */
export function toEngineSlots(
  slots: readonly SlotDto[],
  keyById: ReadonlyMap<string, string>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  const ordered = [...slots].sort(
    (a, b) => a.tierKey.localeCompare(b.tierKey) || a.position - b.position,
  );
  for (const slot of ordered) {
    out[slot.tierKey] ??= [];
    if (slot.csmId === null) continue;
    const key = keyById.get(slot.csmId);
    if (key !== undefined) out[slot.tierKey]!.push(key);
  }
  return out;
}

/** Pins → the engine's `accountId → CsmId`. Untranslatable pins are dropped and reported. */
export function toEnginePins(
  pins: readonly PinDto[],
  keyById: ReadonlyMap<string, string>,
): Pins {
  const out: Record<string, string> = {};
  for (const pin of pins) {
    // A tier-only decision names nobody. Skipping it here is not a dropped pin:
    // its whole content is the tier, and that half is read by toEngineTierPins.
    if (pin.csmId === null) continue;
    const key = keyById.get(pin.csmId);
    if (key !== undefined) out[pin.customerId] = key;
  }
  return out;
}

/**
 * Pins → the engine's `accountId → tierKey`, dropping placements into tiers this
 * configuration does not define.
 *
 * DROPPED AND REPORTED, not passed through. `bucketByTier` THROWS on a placement
 * naming an unknown tier — deliberately, because falling back to the spend band
 * would move a hand-picked account without saying so — and a throw here would
 * take the whole page down over one stale row. So the same rule as
 * `toEnginePins`: filter at the glue layer, and let `diagnose()` say what it
 * filtered. cm-api refuses to create these and refuses to publish a config that
 * would strand them, so seeing one means something has already gone wrong.
 */
export function toEngineTierPins(
  pins: readonly PinDto[],
  tiers: readonly StoredTierDto[],
): TierPins {
  const known = new Set(tiers.map((t) => t.key));
  const out: Record<string, string> = {};
  for (const pin of pins) {
    if (pin.tierKey !== null && known.has(pin.tierKey)) {
      out[pin.customerId] = pin.tierKey;
    }
  }
  return out;
}

/**
 * Everything that makes the computed book less trustworthy than it looks.
 *
 * Rendered, not logged. Each of these is a state where the numbers still add up
 * and are still wrong-ish, so a manager reading a portfolio needs to see them
 * next to it — a silent 704-account total that quietly excludes 23 of them is
 * the exact failure mode the engine's `unallocated` field was added for.
 */
export interface StaffingDiagnostics {
  /** Slots reserved for a hire that has not happened, by tier. */
  vacanciesByTier: Record<string, number>;
  /** Pins whose CSM row id is not in the roster read — the pin is being IGNORED. */
  untranslatablePins: string[];
  /**
   * Customers placed by hand into a tier this configuration does not define.
   *
   * They fall back to their spend band, which is a real and invisible move: a
   * strategic account quietly served at Small's half hour. cm-api refuses to
   * create these AND refuses to publish a config that would strand them, so one
   * appearing here means a rule was written outside those boundaries.
   */
  placementsIntoMissingTiers: string[];
  /** Tiers with a book but no staffed slot. Their accounts land nowhere. */
  unstaffedTiers: { tierKey: string; accounts: number }[];
  /**
   * Owners named on accounts who are neither a sentinel nor on the roster.
   * Their accounts are freely distributable rather than locked, which changes
   * the answer without ever erroring.
   */
  ownersNotOnRoster: string[];
  /** Roster members holding no slot, no pin, and no current book. */
  idleRosterMembers: string[];
  /**
   * People REMOVED from the team who still hold seats or pinned customers.
   *
   * THE MOST DANGEROUS STATE THIS MODULE CAN BE IN, and the only one that is
   * completely silent without this. Removing someone deactivates them, and the
   * roster read returns active people only — so `rosterKeyIndex` never learns
   * their key, `toEngineSlots` and `toEnginePins` cannot translate their row id,
   * and every seat and pin they hold is dropped on the floor. Their tier quietly
   * loses a staffer, their pinned customers quietly rejoin the distribution, and
   * nothing errors. The book that comes out is a plan for a team that does not
   * exist.
   *
   * Detected from `csmActive: false` on the slot and pin DTOs rather than from
   * the roster, because the roster is exactly what does not contain them. cm-api
   * carries the flag on both for this reason.
   *
   * Keyed by ROW ID with the display name the DTO carried — their engine key is
   * unknowable here, for the same reason they are missing in the first place.
   */
  removedButHolding: {
    csmId: string;
    displayName: string;
    seats: number;
    pins: number;
  }[];
  /**
   * People this SCENARIO removed, and what their leaving released.
   *
   * The counterpart of `removedButHolding` and its opposite in every way that
   * matters: that one is a silent, dangerous state to be discovered, this one is
   * a question somebody deliberately asked. It is reported because the two
   * consequences are invisible in the resulting book — a tier is one seat
   * smaller than the team is, and some customers that were deliberately kept
   * with a named person are now wherever the balancer put them.
   */
  departedInScenario: {
    csmId: string;
    displayName: string;
    seats: number;
    /** Customers whose pin to this person was released to the balancer. */
    pinsReleased: number;
  }[];
}

export interface StaffingModel {
  accounts: Account[];
  config: StaffingConfig;
  pins: Pins;
  result: StaffingResult;
  /** Per-CSM load, in the order the engine produced the portfolios. */
  loads: ReturnType<typeof portfolioLoad>[];
  tierLoads: ReturnType<typeof tierLoads>;
  displayName: (key: string) => string;
  diagnostics: StaffingDiagnostics;
}

/**
 * Thrown when the engine refuses the inputs outright (an out-of-order tier list,
 * a pin naming someone off the roster, a duplicated slot). Carries the engine's
 * own message: it names the offending value, and paraphrasing it would lose the
 * one detail that makes it fixable.
 */
export class StaffingModelError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StaffingModelError';
  }
}

/**
 * Build the whole computed model from one read set.
 *
 * `overrides` lets the Overview page move a threshold, an hours target, or the
 * balance mode and re-render without writing anything: the engine recomputes tiering from live
 * spend on every call, which is the property that makes a slider honest. Saving
 * is a separate, explicit POST.
 */
export interface StaffingModelOverrides {
  tiers?: readonly StoredTierDto[];
  balanceBy?: BalanceBy;
  /**
   * Team seats to compute against, instead of the stored ones — "what if this
   * CSM moved to Medium".
   */
  slots?: readonly SlotDto[];
  /**
   * Pins to compute against, instead of the stored ones — "what if this
   * customer went to her".
   *
   * Overrides rather than merges, deliberately: a scenario that could only ADD
   * pins could never answer "what if we UNpinned this one", which is half of
   * every conversation about a pin. The caller composes the list it wants.
   */
  pins?: readonly PinDto[];
  /**
   * People who do not exist, staffing a tier — "what if we hired into Medium".
   *
   * A hire needs no special case in the engine: it is a roster entry plus a
   * seat, and both are already overridable. What it does need is to be added to
   * the engine's ROSTER allow-list as well as its slots, because the engine
   * refuses to distribute to anyone the roster has not declared — a guard that
   * exists precisely so a malformed rule cannot invent a person, and which
   * therefore has to be told when the invention is deliberate.
   */
  hires?: readonly HypotheticalCsm[];
  /**
   * People leaving — `staffing_csm` row ids.
   *
   * Their seats are REMOVED, not vacated, so the tier shrinks and the book
   * redistributes across whoever is left. A vacancy would hold the capacity
   * open and answer a different question ("what if we backfill"), which is this
   * plus a `hires` entry.
   *
   * They also come out of the engine's ROSTER allow-list, which is what makes
   * the pins matter: the engine refuses to distribute to anyone the roster does
   * not declare, so a pin naming a departing CSM would be rejected outright.
   * Those pins release the customer back to the balancer, and any hand-picked
   * TIER on the same pin is kept — which band a customer is served at is a
   * statement about the customer, not about who held it.
   */
  departures?: readonly string[];
}

export function buildStaffingModel(
  read: StaffingReadSet,
  overrides: StaffingModelOverrides = {},
): StaffingModel {
  const storedConfig = read.config.config;
  if (!storedConfig) {
    throw new StaffingModelError(
      'No staffing configuration exists yet. Seed one from the CRM before a book can be computed.',
    );
  }

  const keyById = rosterKeyIndex(read.roster.roster);
  const names = displayNameIndex(
    read.roster.roster,
    read.accounts.csmDisplayNames,
  );

  // Invented people join the same three indexes a real one occupies — key
  // resolution, display name, and the roster allow-list — so that nothing
  // downstream needs a branch for them. Their id IS their engine key.
  const hires = overrides.hires ?? [];
  for (const hire of hires) {
    keyById.set(hire.id, hire.id);
    names.set(hire.id, hire.displayName);
  }

  const departures = new Set(overrides.departures ?? []);

  const tiers = toEngineTiers(overrides.tiers ?? storedConfig.tiers);
  const withHires: SlotDto[] = [
    // Their seats go, rather than emptying — see `departures` above.
    ...(overrides.slots ?? read.slots.slots).filter(
      (slot) => slot.csmId === null || !departures.has(slot.csmId),
    ),
    // At the END of their tier. Position is load-bearing — the engine breaks
    // capacity ties by first slot — so a hire must not reshuffle the existing
    // team's books merely by being considered.
    ...hires.map((hire, i) => ({
      tierKey: hire.tierKey,
      position: 1_000_000 + i,
      csmId: hire.id,
      csmDisplayName: hire.displayName,
      csmActive: true,
      vacancy: false,
    })),
  ];
  const slots = toEngineSlots(withHires, keyById);
  const rosterKeys = [
    ...read.roster.roster
      .filter((m) => !departures.has(m.id))
      .map((m) => csmKeyOf(m)),
    ...hires.map((h) => h.id),
  ];

  const config: StaffingConfig = {
    tiers,
    slots,
    roster: rosterKeys,
    hrsPerFteYear: storedConfig.hrsPerFteYear,
    // The stored value, finally reaching the engine. cm-api has validated and
    // persisted `balanceBy` on every config version since the persistence layer
    // shipped, and until the engine honoured it a manager could select
    // spend-balancing and receive a count-balanced book with no error anywhere.
    // This line is the last half of that fix.
    balanceBy: overrides.balanceBy ?? storedConfig.balanceBy,
  };

  // A pin to somebody who is leaving cannot hold: the engine refuses to
  // distribute to anyone outside the roster it was given. The CSM half is
  // dropped so the customer rejoins the balancer; the TIER half survives, being
  // a statement about the customer rather than about who held it.
  // Counted PER PERSON, not as one list. A shared tally reports the same total
  // against every departure, so modelling two people leaving would tell you each
  // of them released all the pins.
  const departedPins = new Map<string, string[]>();
  const livePins = (overrides.pins ?? read.pins.pins).map((pin) => {
    if (pin.csmId === null || !departures.has(pin.csmId)) return pin;
    const released = departedPins.get(pin.csmId) ?? [];
    released.push(pin.customerId);
    departedPins.set(pin.csmId, released);
    return { ...pin, csmId: null, csmDisplayName: null, csmActive: null };
  });
  const pins = toEnginePins(livePins, keyById);
  const tierPins = toEngineTierPins(
    livePins,
    overrides.tiers ?? storedConfig.tiers,
  );
  const accounts: Account[] = read.accounts.accounts.map((a) => ({
    id: a.id,
    name: a.name,
    spendM: a.spendM,
    currentCsm: a.currentCsm,
  }));

  let result: StaffingResult;
  try {
    result = computePortfolios(accounts, config, pins, tierPins);
  } catch (err) {
    throw new StaffingModelError(
      err instanceof Error ? err.message : String(err),
    );
  }

  // Hours are charged at the tier of EACH account, not the CSM's own tier, so
  // the load math needs the account→tier map the engine just derived.
  const tierOfAccount = new Map<string, string>();
  for (const [tierKey, list] of Object.entries(result.byTier)) {
    for (const account of list) tierOfAccount.set(account.id, tierKey);
  }

  return {
    accounts,
    config,
    pins,
    result,
    loads: result.portfolios.map((p) =>
      portfolioLoad(p, tierOfAccount, config),
    ),
    tierLoads: tierLoads(result.byTier, config),
    displayName: (key: string) => names.get(key) ?? key,
    // Diagnosed against the OVERRIDDEN inputs, not the stored ones. A scenario
    // that moved every CSM out of a tier makes that tier unstaffed, and a
    // diagnostics panel still describing the saved model would be reassuring
    // about a book nobody is looking at.
    diagnostics: {
      ...diagnose(
        {
          ...read,
          slots: { ...read.slots, slots: withHires },
          pins: { ...read.pins, pins: [...(overrides.pins ?? read.pins.pins)] },
        },
        config,
        result,
        keyById,
      ),
      // Computed HERE rather than inside `diagnose`, which is given the
      // post-override read set and so cannot see what was taken out of it.
      departedInScenario: [...departures].map((csmId) => ({
        csmId,
        displayName:
          read.roster.roster.find((m) => m.id === csmId)?.displayName ??
          `CSM ${csmId}`,
        seats: (overrides.slots ?? read.slots.slots).filter(
          (slot) => slot.csmId === csmId,
        ).length,
        pinsReleased: departedPins.get(csmId)?.length ?? 0,
      })),
    },
  };
}

export function diagnose(
  read: StaffingReadSet,
  config: StaffingConfig,
  result: StaffingResult,
  keyById: ReadonlyMap<string, string>,
): StaffingDiagnostics {
  const vacanciesByTier: Record<string, number> = {};
  for (const slot of read.slots.slots) {
    if (slot.vacancy) {
      vacanciesByTier[slot.tierKey] = (vacanciesByTier[slot.tierKey] ?? 0) + 1;
    }
  }

  const untranslatablePins = read.pins.pins
    .filter((p) => p.csmId !== null && !keyById.has(p.csmId))
    .map((p) => p.customerId);

  const definedTiers = new Set(config.tiers.map((t) => t.key));
  const placementsIntoMissingTiers = read.pins.pins
    .filter((p) => p.tierKey !== null && !definedTiers.has(p.tierKey))
    .map((p) => p.customerId);

  const unstaffedTiers = Object.entries(result.unallocated)
    .filter(([, list]) => list.length > 0)
    .map(([tierKey, list]) => ({ tierKey, accounts: list.length }));

  const rosterKeys = new Set(config.roster);
  const ownersNotOnRoster = [
    ...new Set(
      read.accounts.accounts
        .map((a) => a.currentCsm)
        .filter(
          (c): c is string =>
            c !== null && !SENTINEL_CSMS.has(c) && !rosterKeys.has(c),
        ),
    ),
  ].sort();

  // "Idle" means the computed book gave them nothing — no distributed accounts
  // and no pins. That is a legitimate state (a new hire, a manager who only ever
  // holds pins) and NOT an error, but it is the tell for a mis-keyed roster
  // member whose accounts went to a phantom second copy of them.
  const withBooks = new Set(
    result.portfolios
      .filter((p) => p.assigned.length + p.pinned.length > 0)
      .map((p) => p.csm),
  );
  const idleRosterMembers = [...rosterKeys]
    .filter((k) => !withBooks.has(k))
    .sort();

  // Seats and pins held by somebody who has been REMOVED from the team.
  //
  // Read off `csmActive`, which cm-api sets on both DTOs, because the roster
  // read returns active people only — so the roster cannot be consulted about
  // the very people this looks for. Everything they hold has already been
  // silently dropped from the computed book by the time this runs; saying so is
  // the whole job.
  const holding = new Map<
    string,
    { csmId: string; displayName: string; seats: number; pins: number }
  >();
  const note = (
    csmId: string,
    displayName: string | null,
    field: 'seats' | 'pins',
  ) => {
    const entry = holding.get(csmId) ?? {
      csmId,
      displayName: displayName ?? `csm ${csmId}`,
      seats: 0,
      pins: 0,
    };
    entry[field] += 1;
    // A later row may carry the name an earlier one lacked.
    if (displayName) entry.displayName = displayName;
    holding.set(csmId, entry);
  };
  for (const slot of read.slots.slots) {
    if (slot.csmId !== null && slot.csmActive === false)
      note(slot.csmId, slot.csmDisplayName, 'seats');
  }
  for (const pin of read.pins.pins) {
    if (pin.csmId !== null && pin.csmActive === false)
      note(pin.csmId, pin.csmDisplayName, 'pins');
  }

  return {
    // Empty from `diagnose`, always. Only buildStaffingModel knows which people
    // a scenario removed, because by the time the read set reaches here they are
    // already gone from it — which is the whole point of the override.
    departedInScenario: [],
    vacanciesByTier,
    untranslatablePins,
    placementsIntoMissingTiers,
    unstaffedTiers,
    ownersNotOnRoster,
    idleRosterMembers,
    removedButHolding: [...holding.values()].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    ),
  };
}

/** Bucket the book by tier without running a full assignment — used by the tier sliders. */
export function tierPreview(
  accounts: readonly Account[],
  tiers: readonly StoredTierDto[],
  tierPins: TierPins = {},
): Record<string, Account[]> {
  return bucketByTier(accounts, toEngineTiers(tiers), tierPins);
}
